import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useTranslation } from 'react-i18next';

const ProductDetail = () => {
  const { category, id } = useParams();
  const { t } = useTranslation();

  // Fetch product data
  const categoryItems = t(`products.categories.${category}.items`, { returnObjects: true });
  const product = Array.isArray(categoryItems) ? categoryItems.find(item => item.id === id) : null;

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-[#0A1F44] mb-4">
            {t('productDetail.notFound')}
          </h2>
          <Link to="/products">
            <Button className="bg-[#FF6A00] hover:bg-[#E55F00] text-white">
              <ArrowLeft className="mr-2 h-4 w-4" /> {t('productDetail.back')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Normalize specs
  const specifications = product.specs || product.specifications || {};
  const specLabels = product.specLabels || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-[95%] sm:max-w-[90%] md:max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-[#FF6A00] transition-colors">
              {t('nav.home')}
            </Link>
            <span>/</span>
            <Link to="/products" className="hover:text-[#FF6A00] transition-colors">
              {t('nav.products')}
            </Link>
            <span>/</span>
            <Link to={`/products/${category}`} className="hover:text-[#FF6A00] transition-colors">
              {t(`products.categories.${category}.title`)}
            </Link>
            <span>/</span>
            <span className="text-[#0A1F44] font-semibold">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[95%] sm:max-w-[90%] md:max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <Link to={`/products/${category}`}>
          <Button
            variant="outline"
            className="mb-6 sm:mb-8 border-[#0A1F44] text-[#0A1F44] hover:bg-[#0A1F44] hover:text-white transition-all duration-300"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> {t('productDetail.back')}
          </Button>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
          {/* Image */}
          <div className="relative h-64 sm:h-80 md:h-[480px] rounded-lg overflow-hidden shadow-lg bg-gray-100 flex items-center justify-center">
            {product.image ? (
              <img
                src={process.env.PUBLIC_URL + product.image}
                alt={product.name}
                className="w-full h-full object-contain p-4"
              />
            ) : (
              <span className="text-gray-400">No Image Available</span>
            )}
            <div className="absolute top-4 right-4">
              <span className="bg-[#FF6A00] text-white px-4 py-2 rounded-full text-xs sm:text-sm font-semibold shadow-md">
                {t(`products.categories.${category}.title`)}
              </span>
            </div>
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0A1F44] mb-3 sm:mb-4">
              {product.name}
            </h1>
            <p className="text-gray-600 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed">
              {product.shortDesc || product.description}
            </p>

            {/* Description */}
            <div className="bg-white rounded-lg p-5 sm:p-6 shadow-lg mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-[#0A1F44] mb-3">
                {t('productDetail.description')}
              </h2>
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Features (if available) */}
            {product.features && (
              <Card className="border-none shadow-lg mb-6 sm:mb-8">
                <CardHeader>
                  <CardTitle className="text-xl sm:text-2xl text-[#0A1F44]">
                    {t('productDetail.features')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 sm:space-y-3">
                    {product.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-[#FF6A00] mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm sm:text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Specifications */}
            {Object.keys(specifications).length > 0 && (
              <Card className="border-none shadow-lg mb-6 sm:mb-8">
                <CardHeader>
                  <CardTitle className="text-xl sm:text-2xl text-[#0A1F44]">
                    {t('productDetail.specs')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {Object.entries(specifications).map(([key, val]) => (
                      <div
                        key={key}
                        className="bg-gray-50 p-3 sm:p-4 rounded-lg text-center sm:text-left"
                      >
                        <span className="text-xs sm:text-sm text-gray-500 uppercase tracking-wide">
                          {specLabels[key] || key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        <p className="font-semibold text-[#0A1F44] text-sm sm:text-base mt-1">
                          {val}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Components (if available) */}
            {product.components && (
              <Card className="border-none shadow-lg mb-8">
                <CardHeader>
                  <CardTitle className="text-xl sm:text-2xl text-[#0A1F44]">
                    {t('productDetail.components')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                    {product.components.map((section, i) => (
                      <div
                        key={i}
                        className="bg-gray-50 p-3 sm:p-4 rounded-lg text-center hover:bg-gray-100 transition"
                      >
                        <p className="text-[#0A1F44] text-sm sm:text-base font-semibold leading-snug">
                          {section}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a href="mailto:support@hh-enterprise.net" className="flex-1">
                <Button
                  size="lg"
                  className="w-full bg-[#FF6A00] hover:bg-[#E55F00] text-white"
                >
                  <FileText className="mr-2 h-5 w-5" />
                  {t('productDetail.requestQuote')}
                </Button>
              </a>
              <a href="mailto:support@hh-enterprise.net" className="flex-1">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full border-[#0A1F44] text-[#0A1F44] hover:bg-[#0A1F44] hover:text-white"
                >
                  {t('productDetail.contactDetails')}
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Subsystems (Specific to RL54 for now, or if we have images for others) */}
        {id === 'rl54-high-speed-tractor' && product.components && (
          <section className="mt-12 sm:mt-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1F44] mb-6 sm:mb-8 text-center md:text-left">
              {t('productDetail.subsystems')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-8">
              {product.components.map((module, i) => (
                <Card
                  key={i}
                  className="overflow-hidden border-none shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="relative h-40 sm:h-48 overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img
                      src={process.env.PUBLIC_URL + `/RL54-${i + 1}.png`}
                      alt={`Subsystem ${i + 1}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <CardContent className="p-5 sm:p-6">
                    <h3 className="text-base sm:text-lg font-bold text-[#0A1F44] mb-2">
                      {module}
                    </h3>
                    <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                      {t('productDetail.moduleDesc', { number: i + 1 })}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
